import moment from "moment";
import { wfStatus } from "services/workflowsService";
import { planWorkflowDebug, planWorkflowDebugWarn } from "utils/planWorkflowDebug";

const LOG_PREFIX = "[PlanReopenAbsence]";

/** Usa console.log (visible por defecto; console.debug suele estar oculto en DevTools). */
export function planReopenLog(...args) {
	console.log(LOG_PREFIX, ...args);
}

/** Días explícitos en usage (puede ser un subconjunto del rango start–end). */
export function getWorkflowUsageDays(workflow) {
	return (workflow?.usage || []).map((u) => u.day).filter(Boolean);
}

/**
 * Indica si un día pertenece a la ausencia.
 * El API a veces devuelve usage solo con la primera semana; en ese caso
 * se usa el rango start–end para el resto del periodo.
 */
export function workflowCoversDay(workflow, day) {
	if (!day) {
		return false;
	}

	if (getWorkflowUsageDays(workflow).includes(day)) {
		return true;
	}

	if (!workflow?.start) {
		return false;
	}

	const start = moment(workflow.start);
	const end = workflow.end ? moment(workflow.end) : start.clone();

	return moment(day).isBetween(start, end, "day", "[]");
}

/** Días cubiertos por una ausencia (usage del API o rango start–end). */
export function getWorkflowAbsenceDays(workflow) {
	const usageDays = getWorkflowUsageDays(workflow);

	if (usageDays.length) {
		return usageDays;
	}

	if (!workflow?.start) {
		return [];
	}

	const start = moment(workflow.start);
	const end = workflow.end ? moment(workflow.end) : start.clone();
	const days = [];

	for (let d = start.clone(); d.isSameOrBefore(end, "day"); d.add(1, "day")) {
		days.push(d.format("YYYY-MM-DD"));
	}

	return days;
}

export function getWorkflowEmployeeId(workflow) {
	return workflow?.requester?.id ?? workflow?.employee?.id ?? null;
}

/**
 * En una celda, si hay turno real (ausencia, trabajo, etc.) no mostrar también el descanso por defecto.
 */
export function getAssignmentsForDisplay(assignments, defaultShiftId) {
	const list = Array.isArray(assignments) ? assignments : [];

	if (defaultShiftId == null) {
		return list;
	}

	const hasNonDefault = list.some(
		(a) => a.id !== -1 && a.shift?.id != null && a.shift.id !== defaultShiftId
	);

	if (!hasNonDefault) {
		return list;
	}

	return list.filter(
		(a) => a.id !== -1 && a.shift?.id != null && a.shift.id !== defaultShiftId
	);
}

/** Assignments visibles en celda; placeholder de descanso solo si el día está vacío. */
export function resolveDayAssignments(assignments, defaultShift, isActive) {
	let visible = getAssignmentsForDisplay(assignments, defaultShift?.id);

	if (isActive && visible.length === 0 && defaultShift) {
		visible = [{ id: -1, shift: defaultShift }];
	}

	return visible;
}

export function workflowOverlapsPeriod(workflow, start_from, start_to) {
	if (!start_from || !start_to) {
		return true;
	}

	const periodStart = moment(start_from);
	const periodEnd = moment(start_to);

	for (const day of getWorkflowAbsenceDays(workflow)) {
		if (moment(day).isBetween(periodStart, periodEnd, "day", "[]")) {
			return true;
		}
	}

	if (!workflow?.start) {
		return false;
	}

	const ws = moment(workflow.start);
	const we = workflow.end ? moment(workflow.end) : ws;

	return (
		ws.isSameOrBefore(periodEnd, "day") && we.isSameOrAfter(periodStart, "day")
	);
}

export function isApprovedWorkflowStatus(status) {
	return (
		status === wfStatus.approved
	);
}

const PLANNING_ABSENCE_LOOKBACK_DAYS = 365;

/**
 * Rango ampliado para la petición al API: el backend filtra por fecha de inicio
 * de la ausencia, no por solapamiento. Las IT largas que empezaron antes de la
 * semana visible no aparecen si solo se pide el periodo actual.
 */
export function getPlanningAbsenceQueryRange(start_from, start_to) {
	if (!start_from || !start_to) {
		return { start_from, start_to };
	}

	return {
		start_from: moment(start_from)
			.subtract(PLANNING_ABSENCE_LOOKBACK_DAYS, "days")
			.format("YYYY-MM-DD"),
		start_to,
	};
}

export function filterApprovedWorkflows(
	workflows,
	{ employeeIds, start_from, start_to } = {}
) {
	const ids =
		employeeIds instanceof Set ? employeeIds : new Set(employeeIds || []);

	return (workflows || []).filter((w) => {
		const status = w.status || w.workflow_status;
		if (!isApprovedWorkflowStatus(status)) {
			return false;
		}
		if (start_from && start_to && !workflowOverlapsPeriod(w, start_from, start_to)) {
			return false;
		}
		if (ids.size > 0 && !ids.has(getWorkflowEmployeeId(w))) {
			return false;
		}
		return true;
	});
}

/**
 * Ausencias aprobadas del periodo para planificación.
 * Como máximo 2 peticiones: por grupo y, solo si vacío, managed con include_all_company (admin/supervisor).
 */
export async function fetchApprovedWorkflowsForPlanPeriod({
	workflowsService,
	group_id,
	start_from,
	start_to,
	employeeIds,
	includeAllCompany = false,
}) {
	const attempts = [];
	const ids =
		employeeIds instanceof Set ? employeeIds : new Set(employeeIds || []);

	const tryList = (list, source) => {
		const filtered = filterApprovedWorkflows(list, {
			employeeIds: ids,
			start_from,
			start_to,
		});
		return {
			workflows: filtered,
			source,
			rawCount: list?.length ?? 0,
			filteredCount: filtered.length,
		};
	};

	const recordAttempt = (step, response) => {
		const entry = {
			step,
			ok: response.ok,
			httpStatus: response.httpStatus,
			count: response.data?.length ?? 0,
			errors: response.errors,
		};
		attempts.push(entry);
		if (!response.ok) {
			planWorkflowDebugWarn("fetch attempt failed", entry);
		} else {
			planWorkflowDebug("fetch attempt", entry);
		}
	};

	planWorkflowDebug("fetchApprovedWorkflowsForPlanPeriod", {
		group_id,
		start_from,
		start_to,
		employeeCount: ids.size,
		includeAllCompany,
	});

	const queryRange = getPlanningAbsenceQueryRange(start_from, start_to);

	planWorkflowDebug("fetchApprovedWorkflowsForPlanPeriod queryRange", queryRange);

	let response = await workflowsService.getApprovedAbsencesForPlanning({
		group_id,
		start_from: queryRange.start_from,
		start_to: queryRange.start_to,
	});
	recordAttempt("approved+group_id", response);
	if (response.ok && response.data?.length) {
		const result = tryList(response.data, "approved+group_id");
		if (result.filteredCount > 0) {
			return { ...result, attempts };
		}
	}

	if (includeAllCompany) {
		response = await workflowsService.getManagedAbsences({
			start_from: queryRange.start_from,
			start_to: queryRange.start_to,
			page_size: 1000,
			include_all_company: true,
			debugLabel: "plan:managed+include_all_company",
		});
		recordAttempt("managed+include_all_company", response);
		if (response.ok && response.data?.length) {
			const result = tryList(response.data, "managed+include_all_company");
			if (result.filteredCount > 0) {
				return { ...result, attempts };
			}
		}
	}

	return {
		workflows: [],
		source: "none",
		rawCount: 0,
		filteredCount: 0,
		attempts,
	};
}

/** Normaliza el workflow del API para AbsenceEditModal / update. */
export function normalizeWorkflowForEdit(workflow) {
	if (!workflow) {
		return workflow;
	}

	return {
		...workflow,
		status: workflow.status || workflow.workflow_status,
		requester: workflow.requester || workflow.employee,
	};
}

/**
 * Mapa assignmentId → workflow para celdas de planificación vinculadas a ausencias aprobadas.
 */
export function buildWorkflowAbsenceByAssignmentId(plan_assignments, approvedWorkflows) {
	const map = {};

	if (!Array.isArray(plan_assignments) || !Array.isArray(approvedWorkflows)) {
		return map;
	}

	approvedWorkflows.forEach((workflow) => {
		const employeeId = getWorkflowEmployeeId(workflow);
		const shiftId = workflow?.shift?.id;

		if (!employeeId || !shiftId) {
			return;
		}

		plan_assignments.forEach((planAssignment) => {
			if (planAssignment?.employee?.id !== employeeId) {
				return;
			}

			(planAssignment.plans || []).forEach((plan) => {
				const planDay = plan?.day;

				if (!planDay || !workflowCoversDay(workflow, planDay)) {
					return;
				}

				(plan.assignments || []).forEach((assignment) => {
					if (assignment.id === -1) {
						return;
					}

					if (assignment.shift?.id === shiftId) {
						map[assignment.id] = workflow;
					}
				});
			});
		});
	});

	return map;
}

/** Logs de diagnóstico para cruce workflows ↔ planificación. */
export function logWorkflowAbsenceMatchDiagnostics(
	plan_assignments,
	approvedWorkflows,
	map
) {
	const workflows = Array.isArray(approvedWorkflows) ? approvedWorkflows : [];
	const assignments = Array.isArray(plan_assignments) ? plan_assignments : [];
	const linkedIds = Object.keys(map || {});

	planReopenLog( "── Cruce ausencias ↔ plan ──", {
		workflowsFromApi: workflows.length,
		planRows: assignments.length,
		linkedAssignmentIds: linkedIds.length,
		linkedAssignmentIdsSample: linkedIds.slice(0, 10),
	});

	if (workflows.length === 0) {
		planReopenLog( "Sin workflows aprobados en la respuesta del API");
		return;
	}

	workflows.forEach((workflow, i) => {
		const employeeId = getWorkflowEmployeeId(workflow);
		const shiftId = workflow?.shift?.id;
		const days = getWorkflowAbsenceDays(workflow);
		const matches = linkedIds.filter((id) => map[id]?.id === workflow.id);

		planReopenLog( `Workflow #${i + 1}`, {
			workflowId: workflow.id,
			employeeId,
			employeeFields: {
				requesterId: workflow.requester?.id,
				employeeId: workflow.employee?.id,
			},
			shiftId,
			shiftName: workflow.shift?.name,
			days,
			usageCount: workflow.usage?.length ?? 0,
			start: workflow.start,
			end: workflow.end,
			status: workflow.status || workflow.workflow_status,
			matchedAssignmentIds: matches,
		});

		if (matches.length === 0 && employeeId && shiftId) {
			const row = assignments.find((pa) => pa?.employee?.id === employeeId);
			if (!row) {
				planReopenLog( `  Sin fila de empleado ${employeeId} en plan_assignments`);
				return;
			}

			days.forEach((day) => {
				const plan = (row.plans || []).find((p) => p.day === day);
				if (!plan) {
					planReopenLog( `  Día ${day}: no hay celda en el plan`);
					return;
				}

				const cellAssignments = (plan.assignments || []).filter((a) => a.id !== -1);
				planReopenLog( `  Día ${day}: assignments en celda`, cellAssignments.map((a) => ({
					assignmentId: a.id,
					assignmentIdType: typeof a.id,
					shiftId: a.shift?.id,
					shiftName: a.shift?.name,
					shiftType: a.shift?.type,
					shiftMatchesWorkflow: a.shift?.id === shiftId,
				})));
			});
		}
	});
}

/** Detalle de por qué no aparece el botón para la selección actual. */
export function logSelectedWorkflowAbsenceDiagnostics({
	day_selection,
	plan_assignments,
	workflowAbsenceByAssignmentId,
	isPlanSelectionView,
	showActions,
	hasPrivileges,
	planBtnState,
	plan_grid_view,
	planViewMode,
}) {
	const map = workflowAbsenceByAssignmentId || {};
	const mapKeys = Object.keys(map);

	planReopenLog( "── Selección / FloatingBar ──", {
		isPlanSelectionView,
		planViewMode,
		plan_grid_view,
		daySelectionCount: day_selection?.length ?? 0,
		showActions,
		hasPrivileges,
		planBtnState,
		linkedAssignmentsInMap: mapKeys.length,
		noteShowActions:
			"El botón solo se renderiza si showActions=true (privilegios y plan no bloqueado)",
		notePlanSelectionView:
			"Solo vista plan_grid_view=default (1); en grid/horas no aplica",
	});

	if (!isPlanSelectionView) {
		planReopenLog( "Bloqueado: no es vista de selección (default)");
		return;
	}

	if (!showActions) {
		planReopenLog( "Bloqueado: showActions=false (sin privilegios o planBtnState activo)");
	}

	if (!day_selection || day_selection.length !== 1) {
		planReopenLog( "Bloqueado: hace falta exactamente 1 celda seleccionada", {
			day_selection,
		});
		return;
	}

	const { row, index } = day_selection[0];
	const dayPlan = plan_assignments?.[row]?.plans?.[index];
	const employee = plan_assignments?.[row]?.employee;

	if (!dayPlan) {
		planReopenLog( "Bloqueado: celda no encontrada", { row, index });
		return;
	}

	const allAssignments = (dayPlan.assignments || []).filter((a) => a.id !== -1);
	const linked = allAssignments.filter((a) => map[a.id] || map[String(a.id)]);

	planReopenLog( "Celda seleccionada", {
		row,
		index,
		day: dayPlan.day,
		employeeId: employee?.id,
		employeeName: employee ? `${employee.name} ${employee.surname || ""}`.trim() : null,
		assignmentsInCell: allAssignments.length,
		assignmentsDetail: allAssignments.map((a) => ({
			assignmentId: a.id,
			assignmentIdType: typeof a.id,
			inMap: Boolean(map[a.id] || map[String(a.id)]),
			mapLookupByNumber: map[a.id]?.id,
			mapLookupByString: map[String(a.id)]?.id,
			shiftId: a.shift?.id,
			shiftName: a.shift?.name,
			shiftType: a.shift?.type,
		})),
		linkedCount: linked.length,
		linkedWorkflowIds: linked.map((a) => (map[a.id] || map[String(a.id)])?.id),
	});

	if (linked.length === 0) {
		planReopenLog( "Bloqueado: ningún assignment de la celda está en el mapa de workflows");
	} else if (linked.length > 1) {
		planReopenLog( "Bloqueado: más de un assignment vinculado en la misma celda");
	} else {
		planReopenLog( "OK: debería mostrarse Reabrir ausencia (si showActions=true)");
	}
}
